import React from 'react';
import { create } from 'zustand';
import staticSampleData from '../components/SessionDetails/Checkin/sample';
import {
  IconCheckMarkSolid,
  IconPeerReviewSolid,
  IconCheckMarkIndeterminateLine,
  IconXLine,
  IconCommentsOnLine,
} from '@instructure/ui';

export const createCheckinStore = (initialState = {}) =>
  create((set, get) => ({
    stores: {},
    rawGroups: {}, // this the raw response from the API....used for figuring out order etc.
    groups: {},
    items: {},
    activeItem: null,
    statusOptions: {
      ACCEPT: { value: 'accept', icon: <IconCheckMarkSolid /> },
      ACCEPT_COMMENT: {
        value: 'accept-with-comment',
        icon: <IconCommentsOnLine />,
      },
      REJECT: { value: 'reject', icon: <IconXLine /> },
      OPTOUT: { value: 'optout', icon: <IconCheckMarkIndeterminateLine /> },
      UNKNOWN: { value: '', icon: <IconPeerReviewSolid /> },
    },
    checkinActive: false,
    inSequentialFlow: false,
    allowApproval: false,
    hasUnreviewedItems: true,
    isApproved: false,
    isApproving: false,
    isDenyConfirmationOpen: false,
    isCheckinStarted: false,
    trayHeight: null,
    onApproval: () => { },
    onDeny: () => { },

    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    /**
     * Initializes the checkin store with the static sample data. This function iterates
     * over the groups and items, and creates a new object with the required properties.
     * The `rawGroups` property is set to the original static sample data, and the `groups`
     * and `items` properties are set to the new objects. The `approvalControl` property is
     * set to the value from the static sample data. Finally, the `updateProgressStatus`
     * function is called to recalculate the progress status.
     */
    initialize: async () => {
      const groupsObj = {};
      const itemsObj = {};
      const groups = staticSampleData.groups;
      const _temp = JSON.parse(JSON.stringify(staticSampleData.groups));

      for (const group of groups) {
        const _tempGroup = { ...group };

        delete _tempGroup.items;
        _tempGroup.total = group?.items?.length || 0;
        _tempGroup.complete = 0;
        _tempGroup.unreviewed = 0;
        _tempGroup.accepted = 0;
        _tempGroup.optedOut = 0;
        _tempGroup.rejected = 0;

        for (const item of group.items) {
          item.groupId = group.id;
          itemsObj[item.id] = item;
        }

        groupsObj[group.id] = _tempGroup;
      }

      set({
        rawGroups: staticSampleData.groups,
        groups: groupsObj,
        items: itemsObj,
        approvalControl: staticSampleData.approvalControl,
      });

      get().updateProgressStatus();
    },

    /**
     * Toggles the deny confirmation modal on and off. If the value is true,
     * the modal is shown. If the value is false, the modal is hidden.
     *
     * @param {boolean} value - Whether or not the deny confirmation modal should be shown.
     */
    setDenyConfirmationOpen: (value) => {
      set({ isDenyConfirmationOpen: value });
    },

    setTrayHeight: (value) => {
      set({ trayHeight: value });
    },

    /**
     * Sets the active item and toggles the checkinActive flag.
     * @param {Object} item - The item to be set as active.
     */
    setActiveItem: (item) => {
      set({ activeItem: item, checkinActive: true });
      setTimeout(() => {
        get().storeState('layoutStore').adjustSizing();
      }, 50);
    },

    /**
     * Approves the session.
     *
     * Sets the `isApproved` flag to true, clears the active item, and calls the
     * `onSessionApproved` event if it is defined.
     */
    approveSession: async () => {
      set({ isApproving: true });

      try {
        // set the ID as approved...
        await get().storeState('examSessionStore').updateIdentityValidation({ status: 'pass' });

        // call to advance the user's step...
        const advanceStepResponse = await get()
          .storeState('appStore')
          .ApiClient.modifySessionJourneyStep({
            sessionSid: get().storeState('examSessionStore').sessionSid,
            examSid: get().storeState('examSessionStore').examSid,
            installSid: get().storeState('examSessionStore').installSid,
            payload: {
              addedBy: get().storeState('appStore').user.username || "proctor",
              pushUserToNextStep: true,
              completeCurrentStep: true,
            }
          });

        if (advanceStepResponse.successful) {
          set({ isApproved: true, isApproving: false });
          // set the SessionDetails tab to flags if it exists, if not set it to session info
          // the tabs all need to modify first...

          // update the tabs that are showen for session details.
          get().storeState('appStore').updateEnabledComponents({
            sessionDetails: {
              defaultTab: 'flags',
              tabs: ['info', 'identity', 'flags', 'communication']
            },
          });
          get().storeState('appStore').setSessionDetailsTab(['flags', 'info']);

          // now clear all active items...
          get().clearActiveItem();
          // if the onSessionApproved value is a function, go ahead and call it...
          get().storeState('eventStore').trigger('onSessionApproved');
        } else {
          // TODO: some logging or feedback for the user...
          console.error('Failed to advance step:', advanceStepResponse.error);
          set({ isApproving: false });
        }
      } catch (error) {
        console.error('Error approving session:', error);
        set({ isApproving: false });
      }
    },

    stopCheckin: async () => {
      set({ isCheckinStarted: false });
    },

    /**
     * Starts the check-in process for a proctored exam session
     * 
     * Handles the check-in workflow based on the current journey step:
     * - If step is 'WaitingForProctor': Advances the user to the next step and enables chat
     * - If step is 'ConnectedToProctor': Directly enables chat without advancing
     * 
     * When enabled, this function:
     * 1. Updates toolbar buttons to enable the proctor chat
     * 2. Sets the check-in started flag
     * 3. Triggers the 'onCheckinStarted' event
     * 
     * @async
     * @returns {Promise<void>}
     * 
     * @example
     * await startCheckin();
     */
    startCheckin: async () => {
      const currentStep = get().storeState('examSessionStore').getCurrentJourneyStep();

      const enableCheckin = async () => {
        try {
          const actionResult = await get().storeState('examSessionStore').invokeAction({
            action: 'updateToolbarButtons',
            enabled: true,
            buttonNames: ['proctorChat']
          });

          if (actionResult && actionResult.successful) {
            set({ isCheckinStarted: true });
            // if the onCheckinStarted value is a function, go ahead and call it...
            get().storeState('eventStore').trigger('onCheckinStarted');
          } else {
            //TODO: Add some level or error handling...
          }
        } catch (error) {
          console.log('Failed to update toolbar buttons:', error);
        }
      }

      // only do this if the current step is WaitingForProctor, otherwise we may advance too far...
      if (currentStep?.stepId === 'WaitingForProctor') {
        // call to advance the user's step...
        const advanceStepResponse = await get()
          .storeState('appStore')
          .ApiClient.modifySessionJourneyStep({
            sessionSid: get().storeState('examSessionStore').sessionSid,
            examSid: get().storeState('examSessionStore').examSid,
            installSid: get().storeState('examSessionStore').installSid,
            payload: {
              addedBy: get().storeState('appStore').user.username || "proctor",
              pushUserToNextStep: true,
              completeCurrentStep: true,
            }
          });

        if (advanceStepResponse.successful) {
          await enableCheckin();
        } else {
          // TODO: some logging or feedback for the user...
          console.error('Failed to advance step:', advanceStepResponse.error);
        }
      } else if (currentStep?.stepId === 'ConnectedToProctor') {
        // Now enable chat...
        await enableCheckin();
      }

    },

    /**
     * Denies the session during check-in
     * 
     * Sets the session as denied, ends the session with the provided reason and notes,
     * and optionally transitions to cancelled status if reconnection is not allowed.
     * Closes the deny confirmation modal on success.
     * 
     * @param {Object} params - Denial parameters
     * @param {string} params.reasonId - The reason code for denying the session
     * @param {string} params.reasonText - Additional notes explaining the denial
     * @param {string} params.notes - Additional notes explaining the denial
     * @param {string} params.transitionToStatus - Target status ('reconnect' or 'cancelled')
     * @returns {Promise<{successful: boolean, data: any}>} Result of the denial operation
     * 
     * @example
     * const result = await denySession({
     *   reasonId: 'environment-not-compliant',
     *   reasonText: 'Background noise detected',
     *   notes: 'Background noise detected',
     *   transitionToStatus: 'reconnect'
     * });
     */
    denySession: async ({ reasonId, reasonText, notes, transitionToStatus }) => {
      set({ isApproved: false });
      //get().clearActiveItem();

      // call to end the session...
      const endSessionResponse = await get()
        .storeState('examSessionStore')
        .endSession({
          reasonId,
          reasonText,
          notes,
          transitionToStatus,
        });

      if (endSessionResponse.successful) {
        // If the session is set to not allow a reconnect, transition to cancelled...
        if (transitionToStatus === 'cancelled') {
          //await get().storeState('examSessionStore').transitionToCancelled();
        }
        get().setDenyConfirmationOpen(false);
        return { successful: true, data: endSessionResponse.data };
      } else {
        //TODO: show an error or something...
        return { successful: false, data: endSessionResponse.data };
      }
    },

    /**
     * Sets the inSequentialFlow flag, which determines whether the user is
     * in a sequential flow for check-in items. If true, the user is in a
     * sequential flow and will be prompted to approve the session when
     * they complete the last item. If false, the user is not in a sequential
     * flow and the check-in menu will be shown when they complete an item.
     * @param {Boolean} value - The value to set for the inSequentialFlow flag.
     */
    setInSequentialFlow: (value) => {
      set({ inSequentialFlow: value });
    },

    /**
     * Retrieves the next unfinished item in the sequence of check-in items.
     *
     * This function iterates through the raw groups of items and returns the
     * first item with a null status after the currently active item. If no
     * active item is set, it returns the first unfinished item in the sequence.
     *
     * @returns {Object|null} - The next unfinished item or null if all items are complete.
     */

    getNextItem: () => {
      let foundCurrent = false;
      // if no item was passed in, we will just say we found the current one b/c we need the first unfinished....
      if (!get().activeItem) {
        foundCurrent = true;
      }
      for (const group of get().rawGroups) {
        for (const item of group.items) {
          if (foundCurrent && get().items[item.id].status === null) {
            return item;
          }
          if (!foundCurrent && item.id === get().activeItem.id) {
            foundCurrent = true;
          }
        }
      }
      return null;
    },

    /**
     * Clears the active item and sets the checkinActive flag to false.
     *
     * This function is used to reset the check-in state when the user
     * completes an item and the app is not in a sequential flow.
     */
    clearActiveItem: () => {
      set({ activeItem: null, checkinActive: false });
      setTimeout(() => {
        get().storeState('layoutStore').adjustSizing();
      }, 50);
    },

    /**
     * Updates the progress status of the check-in.
     *
     * This function iterates through all the groups and items in the check-in
     * and updates the progress status of each group and the overall check-in.
     * The progress status is determined by the number of completed items in
     * each group and the overall check-in.
     *
     * The function also determines whether the check-in is eligible for approval
     * based on whether all the items in all the groups have been completed.
     */
    updateProgressStatus: () => {
      const updatedItems = { ...get().items };
      const updatedGroups = { ...get().groups };

      let overallTotal = 0;
      let overallComplete = 0;
      let overallUnreviewed = 0;

      // loop all the groups, if they are all complete...then we can say we are eligible...
      Object.keys(updatedGroups).map((groupId) => {
        const tempGroup = updatedGroups[groupId];
        tempGroup.total = 0;
        tempGroup.complete = 0;
        tempGroup.unreviewed = 0;
        tempGroup.accepted = 0;
        tempGroup.optedOut = 0;
        tempGroup.rejected = 0;
        // now loop the items in the group...
        Object.entries(get().getGroupItems(tempGroup.id)).map(
          ([itemId, item]) => {
            tempGroup.total++;
            overallTotal++;

            if (
              item.status === get().statusOptions.ACCEPT_COMMENT.value ||
              item.status === get().statusOptions.ACCEPT.value
            ) {
              tempGroup.accepted++;
            }
            if (item.status === get().statusOptions.REJECT.value) {
              tempGroup.rejected++;
            }
            if (item.status === get().statusOptions.OPTOUT.value) {
              tempGroup.optedOut++;
            }

            if (
              item.status &&
              item.status !== get().statusOptions.REJECT.value
            ) {
              tempGroup.complete++;
              overallComplete++;
            } else if (!item.status) {
              tempGroup.unreviewed++;
              overallUnreviewed++;
            }
          }
        );

        updatedGroups[groupId] = tempGroup;
      });

      set({
        groups: updatedGroups,
        allowApproval: overallTotal === overallComplete,
        hasUnreviewedItems: overallUnreviewed > 0,
      });
    },

    /**
     * This function checks if the approval is allowed.
     * It loops through all the groups and sum up the total items and the completed items.
     * If the total items is equal to the completed items, then approval is allowed.
     * It also checks if there are any unreviewed items and sets the hasUnreviewedItems state accordingly.
     * @returns {boolean} - Whether the approval is allowed.
     */
    checkIfApprovalIsAllowed: () => {
      const groups = get().groups;
      let overallTotal = 0;
      let overallComplete = 0;
      let overallUnreviewed = 0;
      // loop all the groups, if they are all complete...then we can say we are eligible...
      Object.keys(groups).map((groupId) => {
        overallTotal = overallTotal + groups[groupId].total;
        overallComplete = overallComplete + groups[groupId].complete;
        overallUnreviewed = overallUnreviewed + groups[groupId].unreviewed;
      });

      set({
        allowApproval: overallTotal === overallComplete,
        hasUnreviewedItems: overallUnreviewed > 0,
      });

      return overallTotal === overallComplete;
    },

    /**
     * Given a groupId, this function returns an object of items that are associated with the given groupId.
     * The returned object is in the same shape as the items state, with the itemIds as keys and the item objects as the values.
     * @param {string} groupId - The id of the group that the items should be associated with.
     * @returns {Object} - An object of items associated with the given groupId.
     */
    getGroupItems: (groupId) => {
      const filteredObj = Object.keys(get().items).reduce((acc, key) => {
        if (get().items[key].groupId === groupId) {
          acc[key] = get().items[key];
        }
        return acc;
      }, {});

      return filteredObj;
    },

    /**
     * Updates an item in the check-in with the given data.
     * The function also updates the progress status of the check-in.
     * @param {string} itemId - The id of the item to be updated.
     * @param {Object} data - An object containing the data to be updated.
     * The data object should contain a "status" property with the new status of the item.
     */
    updateItem: async (itemId, data) => {
      const updatedItems = { ...get().items };
      /*
    if (data.status && data.status !== get().statusOptions.REJECT.value) {
      updatedGroups[updatedItems[itemId].groupId].complete++;
    } else if (!item.status) {
      updatedGroups[updatedItems[itemId].groupId].unreviewed++;
    }
*/
      updatedItems[itemId].status = data.status;

      set({
        items: updatedItems,
      });
      get().updateProgressStatus();
    },

    findItemById: (id) => {
      for (const group of get().groups) {
        for (const item of group.items) {
          if (item.id === currentItem.id) {
            return item;
          }
        }
      }
      return null;
    },

    /**
     * Sets the active check-in item and opens the check-in drawer if the item is truthy.
     * @param {Object} item - The item to be set as active.
     */
    setActiveCheckinItem: (item) => {
      let openDrawer = false;
      if (item) {
        openDrawer = true;
      }
      set({ activeItem: item, openCheckinDrawer: openDrawer });
    },

    /**
     * Sets the video source used for the check-in player.
     * @param {string} value - The video source to be set.
     */
    setActiveVideoSource: (value) => {
      set({ activeVideoSource: value });
    },
  }));
